import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import { ICooperativeDetails } from "../../exports/interfaces";
import Select from "react-select";
import {
  customMultiSelectStyles,
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import { useOption } from "../../context/OptionContext";
import { CheckboxOption } from "../CheckboxOption";
import NepaliCalendar from "../NepaliCalendar";
import useMobile from "../../hooks/useMobile";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const CooperativeInfo = ({
  currentStep,
  cooperativeInfo,
  setCooperativeInfo,
  errors,
}: {
  currentStep: number;
  cooperativeInfo: ICooperativeDetails;
  setCooperativeInfo: Dispatch<SetStateAction<ICooperativeDetails>>;
  errors: any;
}) => {
  const { categories, statusOptions } = useOption();
  const isMobile = useMobile();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const parseNullableNumber = (value: string) => {
    if (value === "") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getStaffCount = (
    maleMembers: number | null,
    femaleMembers: number | null,
    otherMembers: number | null,
  ) => {
    const values = [maleMembers, femaleMembers, otherMembers];
    const isAllEmpty = values.every((value) => value === null);
    if (isAllEmpty) return null;
    return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setCooperativeInfo((prev: any) => {
      let nextValue =
        name === "maleMembers" ||
        name === "femaleMembers" ||
        name === "otherMembers"
          ? parseNullableNumber(value)
          : value;

      if (name === "mobileNumber") {
        nextValue = value.replace(/\D/g, "").slice(0, 10);
      }

      if (name === "nameNepali") {
        nextValue = value.replace(/[^\u0900-\u097F\s]/g, "");
      }

      if (name === "panNumber") {
        nextValue = value.replace(/\D/g, "").slice(0, 9);
      }

      const nextState = {
        ...prev,
        [name]: nextValue,
      };

      if (
        name === "maleMembers" ||
        name === "femaleMembers" ||
        name === "otherMembers"
      ) {
        const maleMembers =
          name === "maleMembers" ? nextValue : prev.maleMembers;
        const femaleMembers =
          name === "femaleMembers" ? nextValue : prev.femaleMembers;
        const otherMembers =
          name === "otherMembers" ? nextValue : prev.otherMembers;

        return {
          ...nextState,
          staffCount: getStaffCount(maleMembers, femaleMembers, otherMembers),
        };
      }

      return nextState;
    });
  };

  const natureOfWorkOptions =
    categories
      ?.find((item) => item.name === "Nature of Work")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const cooperativeStatuses =
    statusOptions?.find((item) => item.name === "Cooperative Statuses")
      ?.statusOptions || [];

  const cooperativeStatusOptions =
  cooperativeStatuses
    ?.sort((a, b) => a.order - b.order)
    ?.map((item) => ({
      value: item.label,
      label: item.label,
    })) || [];

  const workingAreaOptions =
    categories
      ?.find((item) => item.name === "Working Area")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const registeredAtOptions =
    categories
      ?.find((item) => item.name === "Registration Area")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const yesNoOptions = [
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
  ];
  const womenLeadOptions = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];

  const handleSelectChange = (
    name: keyof ICooperativeDetails,
    selected: SelectOption | null,
  ) => {
    setCooperativeInfo((prev) => ({
      ...prev,
      [name]: selected?.value,
    }));
  };

  const [showCalendar, setShowCalendar] = useState(false);
  const [nepaliRegisteredDate, setNepaliRegisteredDate] = useState("");
  useEffect(() => {
    if (cooperativeInfo) {
      setNepaliRegisteredDate(cooperativeInfo.registeredDate || "mm/dd/yyyy");
    }
  }, [cooperativeInfo]);
  return (
    <>
      <div
        className={`dashboard-card ${currentStep !== 2 ? "mt-2" : ""}`}
        style={currentStep === 2 ? { borderTopLeftRadius: 0 } : {}}
      >
        <div className="row gx-5 gy-4">
          <div className="col-12">
            <div className="row gx-5 gy-4">
              <div
                className={`col-lg-6 col-md-4`}
                data-field="registrationNumber"
              >
                <label className="form-label">
                  Registration Number <span className="primary-color">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.registrationNumber ? "input-error" : ""}`}
                  placeholder="Enter Registration Number"
                  name="registrationNumber"
                  value={cooperativeInfo.registrationNumber}
                  onChange={handleChange}
                />
                {errors.registrationNumber && (
                  <p className="error-message">{errors.registrationNumber}</p>
                )}
              </div>
              <div
                className={`col-lg-6 col-md-4`}
                data-field="businessregistrationNumber"
              >
                <label className="form-label">
                  Business Registration Number{" "}
                  <span className="primary-color">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.businessregistrationNumber ? "input-error" : ""}`}
                  placeholder="Enter Business Registration Number"
                  name="businessregistrationNumber"
                  value={cooperativeInfo.businessregistrationNumber}
                  onChange={handleChange}
                />
                {errors.businessregistrationNumber && (
                  <p className="error-message">
                    {errors.businessregistrationNumber}
                  </p>
                )}
              </div>
            </div>
            <div className="dashboard-card mt-3">
              <h5 className="text-uppercase mb-4">COOPERATIVE IDENTITY</h5>
              <div className="row gx-5 gy-4">
                <div className="col-md-6" data-field="nameNepali">
                  <label className="form-label">
                    Full Cooperative Name in Nepali{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.nameNepali ? "input-error" : ""}`}
                    placeholder="Enter Nepali Name"
                    name="nameNepali"
                    value={cooperativeInfo.nameNepali}
                    onChange={handleChange}
                  />
                  {errors.nameNepali && (
                    <p className="error-message">{errors.nameNepali}</p>
                  )}
                </div>
                <div className="col-md-6" data-field="nameEnglish">
                  <label className="form-label">
                    Full Cooperative Name in English{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.nameEnglish ? "input-error" : ""}`}
                    placeholder="Enter English Name"
                    name="nameEnglish"
                    value={cooperativeInfo.nameEnglish}
                    onChange={handleChange}
                  />
                  {errors.nameEnglish && (
                    <p className="error-message">{errors.nameEnglish}</p>
                  )}
                </div>
                <div className="col-md-3" data-field="natureOfWork">
                  <label className="form-label">
                    Nature of Work <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    options={natureOfWorkOptions}
                    placeholder="Select Nature of Work"
                    isError={Boolean(errors.natureOfWork)}
                    value={
                      natureOfWorkOptions.find(
                        (option) =>
                          option.value === cooperativeInfo.natureOfWork,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      handleSelectChange("natureOfWork", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.natureOfWork && (
                    <p className="error-message">{errors.natureOfWork}</p>
                  )}
                </div>
                <div className="col-md-3" data-field="status">
                  <label className="form-label">
                    Cooperative Status <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    options={cooperativeStatusOptions}
                    placeholder="Select Cooperative Status"
                    isError={Boolean(errors.status)}
                    value={
                      cooperativeStatusOptions.find(
                        (option) => option.value === cooperativeInfo.status,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      handleSelectChange("status", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.status && (
                    <p className="error-message">{errors.status}</p>
                  )}
                </div>
                <div className="col-md-3" data-field="workingArea">
                  <label className="form-label">
                    Working Area <span className="primary-color">*</span>
                  </label>

                  <SelectWithError
                    isMulti
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{
                      Option: CheckboxOption,
                    }}
                    styles={customMultiSelectStyles}
                    options={workingAreaOptions}
                    placeholder="Select Working Area"
                    isError={Boolean(errors.workingArea)}
                    value={workingAreaOptions.filter((option) =>
                      cooperativeInfo.workingArea.includes(option.value),
                    )}
                    onChange={(selected: readonly SelectOption[] | null) =>
                      setCooperativeInfo((prev) => ({
                        ...prev,
                        workingArea: selected
                          ? selected.map((item: SelectOption) => item.value)
                          : [],
                      }))
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.workingArea && (
                    <p className="error-message">{errors.workingArea}</p>
                  )}
                </div>
                <div className="col-md-3" data-field="panNumber">
                  <label className="form-label">
                    Cooperative PAN Number{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={9}
                    className={`form-control ${errors.panNumber ? "input-error" : ""}`}
                    placeholder="Enter Cooperative Pan Number"
                    name="panNumber"
                    value={cooperativeInfo.panNumber}
                    onChange={handleChange}
                  />
                  {errors.panNumber && (
                    <p className="error-message">{errors.panNumber}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="dashboard-card mt-2">
              <h5 className="text-uppercase mb-4">Contact Information</h5>
              <div className="row gx-5 gy-4">
                <div className="col-md-6" data-field="contactPerson">
                  <label className="form-label">
                    Administrator’s Name{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.contactPerson ? "input-error" : ""}`}
                    placeholder="Enter Administrator's Name"
                    name="contactPerson"
                    value={cooperativeInfo.contactPerson}
                    onChange={handleChange}
                  />
                  {errors.contactPerson && (
                    <p className="error-message">{errors.contactPerson}</p>
                  )}
                </div>
                <div className="col-md-6" data-field="mobileNumber">
                  <label className="form-label">
                    Administrator’s Mobile Number{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={10}
                    className={`form-control ${errors.mobileNumber ? "input-error" : ""}`}
                    placeholder="Enter Administrator’s Mobile Number"
                    name="mobileNumber"
                    value={cooperativeInfo.mobileNumber}
                    onChange={handleChange}
                  />
                  {errors.mobileNumber && (
                    <p className="error-message">{errors.mobileNumber}</p>
                  )}
                </div>
                <div className="col-md-6" data-field="email">
                  <label className="form-label">
                    Administrator’s Email{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.email ? "input-error" : ""}`}
                    placeholder="Enter Administrator's Email"
                    name="email"
                    value={cooperativeInfo.email}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <p className="error-message">{errors.email}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="dashboard-card mt-2" style={{ overflow: "unset" }}>
              <h5 className="text-uppercase mb-4">Registration Information</h5>
              <div className="row gx-5 gy-4">
                <div className="col-md-4" data-field="registeredDate">
                  <label className="form-label">
                    Cooperative Registered Date (Nepali)
                    <span className="primary-color">*</span>
                  </label>

                  <div className="dropdown position-relative" ref={dropdownRef}>
                    <button
                      type="button"
                      className={`form-control text-start nepali-date-picker ${
                        errors.registeredDate ? "input-error" : ""
                      }`}
                      onClick={() => setShowCalendar(!showCalendar)}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="text-truncate">
                          {nepaliRegisteredDate || "mm/dd/yyyy"}
                        </span>

                        <i className="fa-regular fa-calendar"></i>
                      </div>
                    </button>

                    {showCalendar && (
                      <div
                        className="dropdown-menu show p-0"
                        style={{
                          width: isMobile ? "249px" : "560px",
                          height: isMobile ? "407px" : "380px",
                          border: "1px solid #ddd",
                          zIndex: 1050,
                        }}
                      >
                        <NepaliCalendar
                          onDateSelect={(nepaliDate) => {
                            setNepaliRegisteredDate(nepaliDate);
                            setCooperativeInfo((prev) => ({
                              ...prev,
                              registeredDate: nepaliDate, // Save Nepali date for backend
                            }));

                            setShowCalendar(false);
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {errors.registeredDate && (
                    <p className="error-message">{errors.registeredDate}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="registeredEnglishDate">
                  <label className="form-label">
                    Cooperative Registered Date (English){" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control custom-date ${errors.registeredEnglishDate ? "input-error" : ""}`}
                    required
                    name="registeredEnglishDate"
                    value={cooperativeInfo.registeredEnglishDate}
                    onChange={handleChange}
                  />
                  {errors.registeredEnglishDate && (
                    <p className="error-message">
                      {errors.registeredEnglishDate}
                    </p>
                  )}
                </div>
                <div className="col-md-4" data-field="registeredAt">
                  <label className="form-label">
                    Registered At <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    options={registeredAtOptions}
                    placeholder="Select Registered At"
                    isError={Boolean(errors.registeredAt)}
                    value={
                      registeredAtOptions.find(
                        (option) =>
                          option.value === cooperativeInfo.registeredAt,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      handleSelectChange("registeredAt", selected)
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.registeredAt && (
                    <p className="error-message">{errors.registeredAt}</p>
                  )}
                </div>
                <div className="col-md-6" data-field="registeredOfficeAddress">
                  <label className="form-label">
                    Registered Office Name and Address{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.registeredOfficeAddress ? "input-error" : ""}`}
                    placeholder="Enter Registered Office Name and Address"
                    name="registeredOfficeAddress"
                    value={cooperativeInfo.registeredOfficeAddress}
                    onChange={handleChange}
                  />
                  {errors.registeredOfficeAddress && (
                    <p className="error-message">
                      {errors.registeredOfficeAddress}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="dashboard-card mt-2">
              <h5 className="text-uppercase mb-4">Operational Information</h5>
              <div className="row gx-5 gy-4">
                <div className="col-md-4" data-field="maleMembers">
                  <label className="form-label">
                    Male Members <span className="primary-color">*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control with-spin ${errors.maleMembers ? "input-error" : ""}`}
                    placeholder="0"
                    name="maleMembers"
                    value={cooperativeInfo.maleMembers || ""}
                    onChange={handleChange}
                  />
                  {errors.maleMembers && (
                    <p className="error-message">{errors.maleMembers}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="femaleMembers">
                  <label className="form-label">
                    Female Members <span className="primary-color">*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control with-spin ${errors.femaleMembers ? "input-error" : ""}`}
                    placeholder="0"
                    name="femaleMembers"
                    value={cooperativeInfo.femaleMembers || ""}
                    onChange={handleChange}
                  />
                  {errors.femaleMembers && (
                    <p className="error-message">{errors.femaleMembers}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="otherMembers">
                  <label className="form-label">
                    Others Members <span className="primary-color">*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control with-spin ${errors.otherMembers ? "input-error" : ""}`}
                    placeholder="0"
                    name="otherMembers"
                    value={cooperativeInfo.otherMembers || ""}
                    onChange={handleChange}
                  />
                  {errors.otherMembers && (
                    <p className="error-message">{errors.otherMembers}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="staffCount">
                  <label className="form-label">
                    Current Working Staffs{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="number"
                    className={`form-control ${errors.staffCount ? "input-error" : ""}`}
                    placeholder="Auto Calculated from Members"
                    name="staffCount"
                    value={cooperativeInfo.staffCount || ""}
                    readOnly
                  />
                  {errors.staffCount && (
                    <p className="error-message">{errors.staffCount}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="softwareUsed">
                  <label className="form-label">
                    Name Of The Software Used{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.softwareUsed ? "input-error" : ""}`}
                    placeholder="Enter Software Used"
                    name="softwareUsed"
                    value={cooperativeInfo.softwareUsed || ""}
                    onChange={handleChange}
                  />
                  {errors.softwareUsed && (
                    <p className="error-message">{errors.softwareUsed}</p>
                  )}
                </div>
                <div className="col-md-4" data-field="isRegisteredInFederation">
                  <label className="form-label">
                    Is Registered in Any Federation ?{" "}
                    <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    options={yesNoOptions}
                    placeholder="Select is Registered in Any Federation ?"
                    isError={Boolean(errors.isRegisteredInFederation)}
                    value={
                      yesNoOptions.find(
                        (opt) =>
                          opt.value ===
                          String(cooperativeInfo.isRegisteredInFederation),
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      setCooperativeInfo((prev) => ({
                        ...prev,
                        isRegisteredInFederation: selected?.value === "true",
                      }))
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.isRegisteredInFederation && (
                    <p className="error-message">
                      {errors.isRegisteredInFederation}
                    </p>
                  )}
                </div>
                <div className="col-md-4" data-field="womenLead">
                  <label className="form-label">
                    Women Lead Cooperative <span className="primary-color">*</span>
                  </label>
                  <SelectWithError
                    styles={customSelectStyles}
                    options={womenLeadOptions}
                    placeholder="Select Women Lead Cooperative"
                    isError={Boolean(errors.womenLead)}
                    value={
                      womenLeadOptions.find(
                        (opt) => opt.value === String(cooperativeInfo.womenLead),
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) =>
                      setCooperativeInfo((prev) => ({
                        ...prev,
                        womenLead: selected?.value || null,
                      }))
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  {errors.womenLead && (
                    <p className="error-message">{errors.womenLead}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CooperativeInfo;
